import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FormattedMessage } from '../../../../util/reactIntl';
import { useParams } from 'react-router-dom';
import WeeklyPlan from '../WeeklyPlan/WeeklyPlan';
import {
  hasDayItineraryInText,
  stripDayItineraryParagraphs,
} from '../WeeklyPlan/weeklyPlanUtils';
import css from './DescriptionSection.module.css';

const FALLBACK_GERMAN_TEXT = 'Übersetzung hinzufügen';

const normalizeText = value => {
  if (value == null || value === FALLBACK_GERMAN_TEXT) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join('\n\n');
  if (typeof value === 'string') return value.replace(/\\n/g, '\n').trim();
  return '';
};

const splitParagraphs = text =>
  text
    .split(/\n{2,}/)
    .map(line => line.trim())
    .filter(Boolean);

const getFirstSentence = paragraph => {
  if (!paragraph) return '';
  const plain = paragraph.replace(/\*\*|__/g, '');
  const dotIndex = plain.indexOf('. ');
  if (dotIndex === -1) {
    return plain.endsWith('.') ? plain : `${plain}.`;
  }
  return plain.slice(0, dotIndex + 1);
};

const MarkdownBlock = ({ text, className, paragraphClassName, blockRef }) => {
  if (!text?.trim()) return null;

  return (
    <div className={className} ref={blockRef}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className={paragraphClassName}>{children}</p>,
          ul: ({ children }) => <ul className={css.markdownList}>{children}</ul>,
          ol: ({ children }) => <ol className={css.markdownList}>{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          strong: ({ children }) => <strong>{children}</strong>,
          em: ({ children }) => <em>{children}</em>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className={css.markdownLink}>
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

const DescriptionSection = ({
  currentListing,
  isExperienceType = false,
  activitiesEn = '',
  activitiesDe = '',
  currentLanguage = 'de',
}) => {
  const { lang } = useParams();
  const [expanded, setExpanded] = useState(false);
  const previewRef = useRef(null);
  const [isPreviewClamped, setIsPreviewClamped] = useState(false);

  const publicData = currentListing?.attributes?.publicData || {};
  const { accomodationDescription, accomodationDescription_de } = publicData;

  const experienceText = useMemo(() => {
    if (lang === 'de') return normalizeText(activitiesDe);
    return normalizeText(activitiesEn);
  }, [activitiesEn, activitiesDe, lang]);

  const accommodationText = useMemo(() => {
    if (accomodationDescription || accomodationDescription_de) {
      return lang === 'en'
        ? normalizeText(accomodationDescription)
        : normalizeText(accomodationDescription_de);
    }
    return '';
  }, [accomodationDescription, accomodationDescription_de, lang]);

  const fullText = isExperienceType ? experienceText : accommodationText;

  const hasWeeklyPlan =
    (Array.isArray(publicData.programDetails?.details) &&
      publicData.programDetails.details.length > 0) ||
    hasDayItineraryInText(fullText);

  const { leadText, previewText, expandedText } = useMemo(() => {
    if (!fullText) {
      return { leadText: '', previewText: '', expandedText: '' };
    }

    let paragraphs = splitParagraphs(fullText);
    if (isExperienceType && hasWeeklyPlan) {
      paragraphs = stripDayItineraryParagraphs(paragraphs);
    }

    if (!paragraphs.length) {
      return { leadText: '', previewText: '', expandedText: '' };
    }

    const lead = getFirstSentence(paragraphs[0]);
    let preview = '';
    let expandedContent = '';

    if (paragraphs.length >= 2) {
      preview = paragraphs[1];
      expandedContent = paragraphs.slice(2).join('\n\n');
    } else {
      const first = paragraphs[0];
      const plain = first.replace(/\*\*|__/g, '');
      const dotIndex = plain.indexOf('. ');
      if (dotIndex !== -1) {
        preview = first.slice(dotIndex + 2).trim();
      }
    }

    return { leadText: lead, previewText: preview, expandedText: expandedContent };
  }, [fullText, isExperienceType, hasWeeklyPlan]);

  const { accommodationPreview, accommodationRest } = useMemo(() => {
    if (isExperienceType || !fullText) {
      return { accommodationPreview: '', accommodationRest: '' };
    }

    const paragraphs = splitParagraphs(fullText);
    if (paragraphs.length <= 1) {
      return { accommodationPreview: fullText, accommodationRest: '' };
    }

    return {
      accommodationPreview: paragraphs[0],
      accommodationRest: paragraphs.slice(1).join('\n\n'),
    };
  }, [fullText, isExperienceType]);

  // The preview paragraph is clamped (see .clampPreview). Measure whether it actually
  // overflows so we only surface the "Show more" toggle when there is hidden text.
  useEffect(() => {
    if (expanded) return undefined;
    const el = previewRef.current;
    if (!el) return undefined;

    const measure = () => {
      setIsPreviewClamped(el.scrollHeight > el.clientHeight + 2);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [expanded, previewText, accommodationPreview, isExperienceType]);

  const hasExpandableContent =
    isExperienceType && (Boolean(expandedText) || hasWeeklyPlan);

  if (!fullText) {
    return null;
  }

  return (
    <section className={css.section} data-elbaction="visible:vis description">
      <h2 className={css.title}>
        {isExperienceType ? (
          <FormattedMessage id="DescriptionSection.experienceTitle" />
        ) : (
          <FormattedMessage id="ListingDetails.descriptionTitle" />
        )}
      </h2>

      {isExperienceType ? (
        <>
          {leadText && (
            <MarkdownBlock text={leadText} className={css.leadWrap} paragraphClassName={css.lead} />
          )}

          {previewText && (
            <MarkdownBlock
              text={previewText}
              className={`${css.bodyText} ${!expanded ? css.clampPreview : ''}`}
              paragraphClassName={css.bodyParagraph}
              blockRef={previewRef}
            />
          )}

          {expanded && (
            <>
              {expandedText && (
                <div className={css.expandedBlock}>
                  <MarkdownBlock
                    text={expandedText}
                    className={css.expandedText}
                    paragraphClassName={css.bodyParagraph}
                  />
                </div>
              )}

              {hasWeeklyPlan && (
                <WeeklyPlan
                  listing={currentListing}
                  currentLanguage={currentLanguage}
                  activitiesText={fullText}
                  embedded
                />
              )}
            </>
          )}

          {(hasExpandableContent || isPreviewClamped) && (
            <button
              type="button"
              className={css.showMoreBtn}
              onClick={() => setExpanded(prev => !prev)}
            >
              {expanded ? (
                <FormattedMessage id="DescriptionSection.showLess" />
              ) : (
                <FormattedMessage id="DescriptionSection.showMore" />
              )}
            </button>
          )}
        </>
      ) : (
        <>
          <MarkdownBlock
            text={accommodationPreview}
            className={`${css.bodyText} ${!expanded ? css.clampPreview : ''}`}
            paragraphClassName={css.bodyParagraph}
            blockRef={previewRef}
          />

          {expanded && accommodationRest && (
            <div className={css.expandedBlock}>
              <MarkdownBlock
                text={accommodationRest}
                className={css.expandedText}
                paragraphClassName={css.bodyParagraph}
              />
            </div>
          )}

          {(accommodationRest || isPreviewClamped) && (
            <button
              type="button"
              className={css.showMoreBtn}
              onClick={() => setExpanded(prev => !prev)}
            >
              {expanded ? (
                <FormattedMessage id="DescriptionSection.showLess" />
              ) : (
                <FormattedMessage id="DescriptionSection.showMore" />
              )}
            </button>
          )}
        </>
      )}
    </section>
  );
};

export default DescriptionSection;
